import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Bot,
  Tags,
  MessageSquare,
  Cpu,
  Wallet,
  ClipboardList,
  Scale,
  Shield,
  FileText,
  ListTodo,
  BookOpen,
  Plug,
  ArrowDownToLine,
  RotateCcw,
  Ticket,
  Menu,
  X,
} from 'lucide-react';
import { Drawer } from '@/components/ui/Drawer';
import { useAuthStore } from '@/lib/stores/auth';
import { canAccessModule } from '@/lib/staffAccess';

interface NavItem {
  labelKey: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredAction: string;
}

const NAV_ITEMS_BASE: Omit<NavItem, 'path'>[] = [
  { labelKey: 'overview', icon: BarChart3, requiredAction: 'stats.view' },
  { labelKey: 'users', icon: Users, requiredAction: 'user.list' },
  { labelKey: 'agents', icon: Bot, requiredAction: 'agent.list' },
  { labelKey: 'capabilities', icon: Tags, requiredAction: 'capability.manage' },
  { labelKey: 'sessions', icon: MessageSquare, requiredAction: 'session.list' },
  // 机甲只读视图（T11）：API 侧 requireAdmin，'mecha.view' 无 staff 授权来源 → 实际仅 admin 可见
  { labelKey: 'mecha', icon: Cpu, requiredAction: 'mecha.view' },
  { labelKey: 'finance', icon: Wallet, requiredAction: 'finance.view' },
  { labelKey: 'withdrawals', icon: ArrowDownToLine, requiredAction: 'finance.view' },
  { labelKey: 'refunds', icon: RotateCcw, requiredAction: 'refund.review' },
  { labelKey: 'promo', icon: Ticket, requiredAction: 'promo.review' },
  { labelKey: 'auditLogs', icon: ClipboardList, requiredAction: 'audit.view' },
  { labelKey: 'reconciliation', icon: Scale, requiredAction: 'finance.view' },
  { labelKey: 'staff', icon: Shield, requiredAction: 'staff.list' },
  { labelKey: 'proposals', icon: FileText, requiredAction: 'proposal.review' },
  { labelKey: 'tasks', icon: ListTodo, requiredAction: 'task.view' },
  { labelKey: 'docs', icon: BookOpen, requiredAction: 'docs.list' },
  { labelKey: 'mcp', icon: Plug, requiredAction: 'mcp.docs.edit' },
];

export default function AdminSidebar() {
  const location = useLocation();
  const { locale } = useParams<{ locale: string }>();
  const { t } = useTranslation('admin', { keyPrefix: 'sidebar' });
  const { user } = useAuthStore();
  const prefix = `/${locale}`;
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV_ITEMS: NavItem[] = NAV_ITEMS_BASE
    .filter((item) => canAccessModule(user, item.requiredAction))
    .map((item) => ({
      ...item,
      path: item.labelKey === 'overview'
        ? `${prefix}/dashboard/admin`
        : `${prefix}/dashboard/admin/${item.labelKey === 'auditLogs' ? 'audit' : item.labelKey === 'mcp' ? 'mcp-permissions' : item.labelKey}`,
    }));

  function isActive(href: string) {
    if (href === `${prefix}/dashboard/admin`) return location.pathname === href;
    return location.pathname.startsWith(href);
  }

  const navList = (onClose?: () => void) => (
    <nav className="px-2 space-y-0.5">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive(item.path)
                ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
                : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground'
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(item.labelKey)}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-border bg-surface-0 hover:bg-surface-2 transition-colors mb-4"
      >
        <Menu className="h-4 w-4" />
        {t('title')}
      </button>

      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-56 shrink-0 border-r border-border bg-surface-0 min-h-[calc(100vh-4rem)]">
        <div className="px-4 py-5">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('title')}
          </h2>
        </div>
        {navList()}
      </aside>

      {/* Mobile drawer */}
      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} side="left">
        <div className="pt-2">
          <div className="flex items-center justify-between px-4 pb-2">
            <h2 className="text-sm font-semibold">{t('title')}</h2>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-1 rounded-md text-muted-foreground hover:bg-surface-2"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {navList(() => setMobileOpen(false))}
        </div>
      </Drawer>
    </>
  );
}
